import { classnames } from '@aibees/shared/utils';
import { Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo, useCallback, useMemo } from 'react';
import { CustomPopover, usePopover } from '../custom-popover';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import * as Styled from './styles';
import * as SX from './sx';
import type {
  PopoverListButtonItem,
  PopoverListButtonProps as Props,
} from './types';

export const PopoverListButton = memo(
  ({
    className,
    items,
    itemSx,
    itemDivider,
    icon,
    variant,
    size,
    buttonElement,
    buttonProps,
    closeAfterSelect,
    onSelect,
    anchorOrigin,
    transformOrigin,
    state,
  }: Props) => {
    const internalPopover = usePopover();
    const popover = state ?? internalPopover;

    const memoizedIcon = useMemo(
      () => <Icon name={icon || 'more-vertical'} size={16} />,
      [icon]
    );

    const popoverElement = useMemo(
      () =>
        buttonElement ?? (
          <IconButton
            variant={variant || 'text'}
            color="default"
            icon={memoizedIcon}
            size={size || 'small'}
            {...buttonProps}
          />
        ),
      [buttonElement, variant, memoizedIcon, size, buttonProps]
    );

    const handleClick = useCallback(
      (
        x: PopoverListButtonItem,
        e: React.MouseEvent<HTMLLIElement, MouseEvent>
      ) => {
        if (closeAfterSelect !== false) popover.onClose();
        if (x.disabled) return null;
        onSelect?.(x, popover, e);
      },
      [closeAfterSelect, onSelect, popover]
    );

    return (
      <Styled.Wrapper className={classnames('PopoverListButton', className)}>
        <CustomPopover
          state={popover}
          element={popoverElement}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          {items.map((x) => (
            <Styled.MenuItem
              className={classnames({
                divider: itemDivider,
                disabled: x.disabled,
              })}
              key={x.key}
              sx={mergeSx(itemSx, SX.menuItem)}
              disabled={popover.loading || x.disabled}
              onClick={(e) => handleClick(x, e)}
            >
              <Styled.ItemWrapper>
                <Styled.TitleWrapper>
                  {x.icon && <Icon name={x.icon} size={20} color="grey.500" />}
                  <Typography variant="body4" color="grey.700">
                    {x.label}
                  </Typography>
                </Styled.TitleWrapper>

                {x.endItem}
              </Styled.ItemWrapper>
            </Styled.MenuItem>
          ))}
        </CustomPopover>
      </Styled.Wrapper>
    );
  }
);

PopoverListButton.displayName = 'PopoverListButton';
